import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiPost } from "../lib/api.js";

/**
 * One `isSensitive` answer — a NIN, a bank account number.
 *
 * These values are encrypted at rest and are the only ones never returned with the submission, so
 * the row starts as a placeholder rather than a value. Before this existed the field was left out
 * of the response altogether, which made an encrypted answer and an unanswered question look
 * identical to staff.
 *
 * Revealing is a superadmin action that writes an audit row naming the field and the stated reason,
 * so the reason box is not decoration: it is the text that answers "why did someone read this
 * applicant's NIN" years later. The value lives in this component's state only — closing the drawer
 * or reloading loses it, and reading it again means another audited request.
 */
export const SensitiveAnswer = ({
  submissionId,
  fieldKey,
  canReveal,
}: {
  submissionId: string;
  fieldKey: string;
  canReveal: boolean;
}): JSX.Element => {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");

  const reveal = useMutation({
    mutationFn: () =>
      apiPost<{ values: Record<string, string>; unanswered: string[] }>(
        `/submissions/${submissionId}/reveal`,
        { reason: reason.trim(), fieldKeys: [fieldKey] },
      ),
    onSuccess: () => setAsking(false),
  });

  if (reveal.isSuccess) {
    const value = reveal.data.values[fieldKey];
    return (
      <span className="break-words">
        {value ?? <span className="text-muted-foreground">Not answered</span>}
        <span className="ml-2 text-xs text-muted-foreground">(revealed · this view only)</span>
      </span>
    );
  }

  if (!canReveal) {
    return <span className="text-muted-foreground">Encrypted — superadmin only</span>;
  }

  if (!asking) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">••••••••</span>
        <button
          type="button"
          onClick={() => setAsking(true)}
          className="rounded-md border px-2 py-0.5 text-xs"
        >
          Reveal
        </button>
      </span>
    );
  }

  return (
    <span className="flex flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2">
        <input
          aria-label="Reason for revealing this value"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why do you need this? (recorded)"
          className="w-64 rounded-md border px-2 py-0.5 text-xs"
        />
        <button
          type="button"
          disabled={reason.trim().length < 5 || reveal.isPending}
          onClick={() => reveal.mutate()}
          className="rounded-md bg-primary px-2 py-0.5 text-xs text-primary-foreground disabled:opacity-60"
        >
          {reveal.isPending ? "Revealing…" : "Confirm"}
        </button>
        <button
          type="button"
          onClick={() => {
            setAsking(false);
            setReason("");
          }}
          className="rounded-md border px-2 py-0.5 text-xs"
        >
          Cancel
        </button>
      </span>
      {reveal.isError && (
        <span role="alert" className="text-xs text-brand-red">
          {reveal.error instanceof Error ? reveal.error.message : "Could not reveal that value."}
        </span>
      )}
    </span>
  );
};
